import { useTranslation } from 'react-i18next';
import { CommissionContract } from '../../api/ComissionContractsAPI';
import Pill from '../Pill/Pill';
import Tooltip from '../Tooltip/Tooltip';
import { paymentStatusToAbbreviatedTranslationKey, paymentStatusToPillColor, paymentStatusToTranslationKey } from '../../shared/paymentStatusToTranslationKey';
import { commissionStatusToAbbreviatedTranslationKey, commissionStatusToPillColor, commissionStatusToTranslationKey } from '../../utils/commission-status';
import { CommissionStatus } from '../../types/commission-statuses';
import { PaymentStatus } from '../../types/payment-statuses';
import { Calendar, Package, FileText, Hash } from 'lucide-react';

interface CommissionContractSearchResultProps {
  commission: CommissionContract;
  onView: (commission: CommissionContract) => void;
}

export default function CommissionContractSearchResult({ commission, onView }: CommissionContractSearchResultProps) {
  const { t } = useTranslation();

  const handleClick = () => {
    onView(commission);
  };

  const fullName = [commission.brideName, commission.brideSurname].filter(Boolean).join(' ');
  const displayName = fullName || t("commission_track_all_page.table.no_data");

  return (
    <div 
      className="group p-3 sm:p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={t("commission_track_all_page.search_result.view_commission_aria", { name: displayName }) as string}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-3 sm:space-y-0 sm:space-x-4">
        <div className="flex-1 min-w-0">
          {/* Header Row */}
          <div className="flex items-center space-x-3 mb-2">
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-gray-900 truncate">
                {displayName}
              </h4>
              {commission.dress && (
                <p className="text-xs text-gray-600 truncate mt-1">
                  {commission.dress}
                </p>
              )}
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              <Tooltip content={t(commissionStatusToTranslationKey(commission.status as CommissionStatus))}>
                <Pill 
                  text={t(commissionStatusToAbbreviatedTranslationKey(commission.status as CommissionStatus))} 
                  color={commissionStatusToPillColor(commission.status as CommissionStatus)}
                />
              </Tooltip>
              <Tooltip content={t(paymentStatusToTranslationKey(commission.payment as PaymentStatus))}>
                <Pill 
                  text={t(paymentStatusToAbbreviatedTranslationKey(commission.payment as PaymentStatus))} 
                  color={paymentStatusToPillColor(commission.payment as PaymentStatus)}
                />
              </Tooltip>
            </div>
          </div>

          {/* Details Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-xs text-gray-600">
            {(commission.submissionDate || commission.endDate) && (
              <div className="flex flex-col space-y-1">
                {commission.submissionDate && (
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-3 h-3 flex-shrink-0 text-gray-400" />
                    <span className="font-medium">{t("commission_track_all_page.search_result.submission_date")}:</span>
                    <span className="truncate">
                      {new Date(commission.submissionDate).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {commission.endDate && (
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-3 h-3 flex-shrink-0 text-gray-400" />
                    <span className="font-medium">{t("commission_track_all_page.search_result.end_date")}:</span>
                    <span className="truncate">
                      {(() => {
                        try {
                          return new Date(commission.endDate).toLocaleDateString();
                        } catch (e) {
                          return commission.endDate;
                        }
                      })()}
                    </span>
                  </div>
                )}
              </div>
            )}
            {commission.contractNumber && (
              <div className="flex items-center space-x-2">
                <Hash className="w-3 h-3 flex-shrink-0 text-gray-400" />
                <span className="font-medium">{t("commission_track_all_page.search_result.contract_number")}:</span>
                <span className="truncate">{commission.contractNumber}</span>
              </div>
            )}
            {commission.contractReference && (
              <div className="flex items-center space-x-2">
                <FileText className="w-3 h-3 flex-shrink-0 text-gray-400" />
                <span className="font-medium">{t("commission_track_all_page.search_result.contract_reference")}:</span>
                <span className="truncate">{commission.contractReference}</span>
              </div>
            )}
            {commission.desiredSalePriceAmount && (
              <div className="flex items-center space-x-2">
                <Package className="w-3 h-3 flex-shrink-0 text-gray-400" />
                <span className="font-medium">{t("commission_track_all_page.search_result.sale_price")}:</span>
                <span className="truncate">
                  {commission.desiredSalePriceAmount.toLocaleString('pl-PL')} {commission.desiredSalePriceCurrency || 'EUR'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

